import { QueryClientProvider } from "@tanstack/react-query";
import i18next from "i18next";
import { lazy, useEffect, useLayoutEffect } from "react";
import { initReactI18next } from "react-i18next";
import { createBrowserRouter, RouterProvider, useNavigate } from "react-router-dom";
import PageNotFound from "./components/page-not-found/page-not-found";
import RootLayout from "./components/root-layout/root-layout";
import assamesseTranslation from "./locales/as/translation.json";
import bangaliTranslation from "./locales/bn/translation.json";
import englishTranslation from "./locales/en/translation.json";
import gujaratiTranslation from "./locales/gu/translation.json";
import hindiTranslation from "./locales/hi/translation.json";
import kannadaTranslation from "./locales/kn/translation.json";
import malayalamTranslation from "./locales/ml/translation.json";
import marathiTranslation from "./locales/mr/translation.json";
import odriyaTranslation from "./locales/or/translation.json";
import tamilTranslation from "./locales/ta/translation.json";
import telguTranslation from "./locales/te/translation.json";
import { hideNativeLoader } from "./shared/appInterface";
import withSuspense from "./shared/hocs/withSuspense";
import { queryClient } from "./shared/http-client";
import { Exit } from "./modules/exit-loader/exit";
import {
    IS_COMMUNITY_ONBOARDING_SESSION_KEY,
    WEB_QUERY_PARAMS,
    WEB_QUERY_PARAM_VALUES,
} from "./shared/constants";
import { useWebQueryParamStore } from "./shared/stores/query-param-store";
import CommunityOnboarding from "./modules/community-onboarding/community-onboarding";
import ParseResume from "./modules/resume-parse/resume-parse";

i18next
    .use(initReactI18next) // passes i18n down to react-i18next
    .init({
        // the translations
        // (tip move them in a JSON file and import them,
        // or even better, manage them via a UI: https://react.i18next.com/guides/multiple-translation-files#manage-your-translations-with-a-management-gui)
        resources: {
            en: {
                translation: englishTranslation,
            },
            ang: {
                translation: hindiTranslation,
            },
            ta: {
                translation: tamilTranslation,
            },
            te: {
                translation: telguTranslation,
            },
            ml: {
                translation: malayalamTranslation,
            },
            kn: {
                translation: kannadaTranslation,
            },
            bn: {
                translation: bangaliTranslation,
            },
            gu: {
                translation: gujaratiTranslation,
            },
            mr: {
                translation: marathiTranslation,
            },
            or: {
                translation: odriyaTranslation,
            },
            as: {
                translation: assamesseTranslation,
            },
        },
        lng: "en", // if you're using a language detector, do not define the lng option
        fallbackLng: "en",

        interpolation: {
            escapeValue: false, // react already saves from xss => https://www.i18next.com/translation-function/interpolation#unescape
        },
    });

const Experience = withSuspense(lazy(() => import("./modules/experience/experience")));

const AboutMe = withSuspense(lazy(() => import("./modules/about-me/about-me")));

const Preferences = withSuspense(lazy(() => import("./modules/preferences/preferences")));
const Education = withSuspense(lazy(() => import("./modules/education/education")));
const MissingEntities = withSuspense(
    lazy(() => import("./modules/missing-entities/missing-entities"))
);
const TigerEyeMissingEntity = withSuspense(
    lazy(
        () =>
            import(
                "./modules/missing-entity/tiger-eye-missing-entity/tiger-eye-missing-entity"
            )
    )
);
const SidPlayground = withSuspense(lazy(() => import("./components/shahid-playground")));
const ShyamPlayground = withSuspense(lazy(() => import("./components/shyam-playground")));
const RecruiterActions = withSuspense(
    lazy(() => import("./modules/recruiter-actions/recruiter-actions"))
);
const Email = withSuspense(
    lazy(() => {
        return import("./modules/email/email");
    })
);

const router = createBrowserRouter([
    {
        path: "/",
        element: <RootLayout />,
        children: [
            {
                path: "",
                element: <AboutMe />,
            },
            {
                path: "/about-me/*",
                element: <AboutMe />,
            },
            {
                path: "/experience/*",
                element: <Experience />,
            },
            {
                path: "/education/*",
                element: <Education />,
            },
            {
                path: "/community-onboarding/*",
                element: <CommunityOnboarding />,
            },
            {
                path: "/shahid",
                element: <SidPlayground />,
            },
            {
                path: "/shyam",
                element: <ShyamPlayground />,
            },
            {
                path: "/preferences/*",
                element: <Preferences />,
            },
            {
                path: "/missing-entities",
                element: <MissingEntities />,
            },
            {
                path: "/missing-entity/tiger-eye/*",
                element: <TigerEyeMissingEntity />,
            },
            {
                path: "/success",
                element: <Exit />,
            },
            {
                path: "/exit",
                element: <Exit />,
            },
            {
                path: "/recruiter-actions/*",
                element: <RecruiterActions />,
            },
            {
                path: "/email/*",
                element: <Email />,
            },
            {
                path: "*",
                element: <PageNotFound />,
            },
        ],
    },
]);

export function App() {
    const { setNBNudgeEnabled, isNBNudgeEnabled } = useWebQueryParamStore(
        (state) => state
    );
    const queryParams = new URLSearchParams(window.location.search);
    const isSourceAvailable = queryParams.get(WEB_QUERY_PARAMS.WEB_INVOCATION_TYPE);
    const isRevisedRnRSourceAvailable = queryParams.has(
        WEB_QUERY_PARAMS.REVISED_RNR_SOURCE
    );
    (window as any).getAppData = (appData: any) => {
        const appDataJson = JSON.parse(appData);
        const event = new CustomEvent(`${appDataJson.type}`, {
            detail: appDataJson?.data,
        });
        window.dispatchEvent(event);
    };

    useEffect(() => {
        try {
            hideNativeLoader();
        } catch (error) {
            console.log("hideNativeLoader not found");
        }
    }, []);

    useLayoutEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const isNBFlagAvailable = queryParams.get(WEB_QUERY_PARAMS.WEB_INVOCATION_TYPE);
        setNBNudgeEnabled(
            !!isNBFlagAvailable &&
                isNBFlagAvailable === WEB_QUERY_PARAM_VALUES.NB_REVISED_RNR
        );
    }, []);

    useLayoutEffect(() => {
        if (!!isSourceAvailable) {
            if (
                !isNBNudgeEnabled &&
                !!isSourceAvailable &&
                isSourceAvailable === WEB_QUERY_PARAM_VALUES.NB_REVISED_RNR
            ) {
                setNBNudgeEnabled(true);
            }
            if (
                !!isSourceAvailable &&
                isSourceAvailable === WEB_QUERY_PARAM_VALUES.COMMUNITY_ONBOARDING
            ) {
                sessionStorage.setItem(IS_COMMUNITY_ONBOARDING_SESSION_KEY, "true");
                window.location.href = "/community-onboarding/";
            }
        }
        if (!!isRevisedRnRSourceAvailable) {
            if (
                queryParams.get(WEB_QUERY_PARAMS.REVISED_RNR_SOURCE) ===
                WEB_QUERY_PARAM_VALUES.TIGER_EYE_REVISED_RNR
            ) {
                window.location.href = "/missing-entity/tiger-eye/";
            }
        }
    }, [isNBNudgeEnabled, isSourceAvailable, isRevisedRnRSourceAvailable]);

    return (
        <QueryClientProvider client={queryClient}>
            {/* <RootLayout> */}
            <RouterProvider router={router} />
            {/* </RootLayout> */}
        </QueryClientProvider>
    );
}
